import i18n from '@/i18n'

/**
 * Reading the backend's own words in the reader's language.
 *
 * A License CR carries `status.phase` as `Valid`, `Expired`, `Trial` and so on,
 * and `status.licenseType` as `trial`/`standard`/`enterprise`; a Flow CR carries
 * its own phase. Every screen used to print those straight out, so a console set
 * to Chinese showed "状态: Valid" and "Enterprise 方案" with English sitting in
 * the middle of translated rows.
 *
 * These are closed vocabularies the platform defines, not free text, so they can
 * be translated. Anything the person typed -- a request's approval reason, a
 * project's description -- is data and stays exactly as written.
 *
 * A value with no translation falls back to itself, which is the right answer
 * for a phase the operator adds before the locale files catch up.
 */

const translate = (key: string, value: string | undefined | null, fallback: string): string => {
  const raw = (value ?? '').trim()
  if (!raw) return fallback
  return i18n.t(`common:${key}.${raw}`, { defaultValue: raw })
}

/** `status.phase` of a License CR: Valid, Expired, Invalid, Trial, FlowLimitExceeded. */
export function licensePhaseLabel(phase: string | undefined | null): string {
  return translate('licensePhase', phase, i18n.t('common:licensePhase.Unknown', { defaultValue: 'Unknown' }))
}

/** `status.licenseType` of a License CR: trial, standard, enterprise. */
export function licenseTypeLabel(type: string | undefined | null, fallback = 'N/A'): string {
  return translate('licenseType', type, fallback)
}

/** `status.phase` of a Flow CR: Ready, Running, Progressing, Pending, Error, Failed. */
export function flowPhaseLabel(phase: string | undefined | null): string {
  return translate('flowPhase', phase, i18n.t('common:flowPhase.Unknown', { defaultValue: 'Unknown' }))
}
